import { FieldTypes } from "./field.types";
import { BaseModel } from "./base.model";
import { fieldsCache, fieldsWithInternalCache, isEmpty } from "../libs/utils";
import {
  FIELD_TYPES,
  FLOW_TYPE,
  MODEL_TO_PARENT_MODEL,
  SYSTEM_MODELS,
  COLUMN_TYPES,
  RHS_TYPE,
  WIDGET_TYPE_FIELDS,
  DATA_TYPES,
  FIELD_ID,
  CHILDTABLE_ID
} from "../constants";
import { MetaDataStore } from "./datastore";

const excludedNames = [
  { Id: "Security" },
  { Id: "Parent" },
  { Id: "Appearance" },
  { Id: "Process:" },
  { Id: "RootProcessDef" },
  { Id: "FlowType" },
  { Id: "Root" },
  { Id: "Suspended" },
  { Id: "Id" },
  { Id: "ModelId" },
  { Id: "ModelName" },
  { Id: "BaseMetadata" },
  { Id: "Fields" },
  { Id: "self" },
  { Id: "_last_saved_at" },
  { Id: "PublishedAt" },
  { Id: "_meta_version" },
  { Id: "Query" },
  { Id: "LastRunQuery" },
  { Id: "DataSources" },
  { Id: "_last_run_at" },
  { Id: "PublishedQuery" },
  { Id: "_published_query_list" },
  { Id: "Views" }
].concat(Object.values(FIELD_TYPES).map((fieldType) => ({ Id: fieldType })));

export function FormMetadataWrapper(jsonData, id = jsonData["Root"]) {
  let kind = jsonData[id].Kind;
  let dynamicClass = FormModel.getMetadata(kind);
  let ds = new MetaDataStore(jsonData);
  let modelIns = new dynamicClass(ds, id, dynamicClass);
  ds.cache.set(id, modelIns);
  return modelIns;
}

export function registerFormModels() {
  FormModel.registerModel(FormModel, "Model"); //self register the baseModel
  FormModel.registerModel(Row);
  FormModel.registerModel(Column);
  FormModel.registerModel(Field);
  FormModel.registerModel(Expression);
  FormModel.registerModel(Node);
  FormModel.registerModel(Appearance);
  FormModel.registerModel(QueryDefinition);
  FormModel.registerModel(Criteria);
  FormModel.registerModel(Condition);
  FormModel.registerModel(Property);
  FormModel.registerModel(RemoteLookup);
  FormModel.registerModel(Event);
}

export class Row extends BaseModel {
  getIndex() {
    return this.getModel(this.getValue("Column"))
      .getRows()
      .findIndex((row) => row.id === this.id);
  }

  getColumns() {
    return this.getList("Row", "Column");
  }
}

Row.setFieldTypes({
  Model: FieldTypes.kindOf("Model"),
  Insight: FieldTypes.kindOf("Model"),
  Button: FieldTypes.kindOf("Model"),
  Column: FieldTypes.kindOf("Column")
});
Row.setOneToManyList(["Row::Column"]);
Row.setManyToOneList([
  "Model::Model", // will be used to create/delete main row
  "Column::Column" //will be used to create/delete inside section/table row
]);

export class Column extends BaseModel {
  isSystemColumn() {
    return Boolean(this.getValue("IsSystem"));
  }

  validate() {
    var errors = {};
    var childErrors = {};
    errors[this.getValue("Id")] = childErrors;

    var propertyErrors = this.validateProperties();
    var oneToManyErrors = this.validateOneToMany();
    if (propertyErrors.length > 0) {
      childErrors["Properties"] = propertyErrors;
    }
    if (!isEmpty(oneToManyErrors)) {
      childErrors["Children"] = oneToManyErrors;
    }
    if (!isEmpty(childErrors)) {
      childErrors["Id"] = this.getValue("Id");
      childErrors["Type"] = this.getValue("Type");
    }

    return errors;
  }
}

Column.setFieldTypes({
  Start: FieldTypes.number,
  End: FieldTypes.number,
  Row: FieldTypes.kindOf("Row"),
  Type: FieldTypes.string.isRequired,
  Name: FieldTypes.string,
  Label: FieldTypes.string, // process button label
  Description: FieldTypes.string,
  Hint: FieldTypes.string, // field hint
  Width: FieldTypes.string, //save child table column resize width
  IsHidden: FieldTypes.boolean // for default visibility of column
});
Column.setOneToManyList(["ColumnVisibility::Criteria"]);
Column.setManyToOneList([
  "Row::Row", //will be used to create/delete column
  "Criteria::Criteria" // will be used to create/delete column visibility
]);

export class Field extends BaseModel {
  setType(fieldType) {
    return this.update("Type", fieldType);
  }

  getType() {
    return this.getValue("Type");
  }

  setWidget(widgetType) {
    return this.update("Widget", widgetType);
  }

  getWidget() {
    return this.getValue("Widget") || this.getValue("Type");
  }

  getQueryDef() {
    return this.getList("Field", "QueryDefinition");
  }

  getRemoteLookupDef() {
    return this.getList("Field", "RemoteLookup")[0];
  }

  isSystemField() {
    return Boolean(this.getValue("IsSystem"));
  }

  getFieldColumn() {
    return this.get("Column");
  }

  getEvents() {
    return this.getList("Field", "Event");
  }

  isPublishedField() {
    let fieldCreateAt = new Date(this.getValue("CreatedAt"));
    let model = this.getModel(this.getValue("Model"));
    let lastPublishedAt = new Date(model.getPublishedAt());
    if (!model.getPublishedAt()) {
      return false;
    } else if (fieldCreateAt > lastPublishedAt) {
      return false;
    }
    return true;
  }

  validate() {
    var childErrors = {};

    var propertyErrors = this.validateProperties();
    var oneToManyErrors = this.validateOneToMany();
    if (propertyErrors.length > 0) {
      childErrors["Properties"] = propertyErrors;
    }
    if (!isEmpty(oneToManyErrors)) {
      childErrors["Children"] = oneToManyErrors;
    }
    if (!isEmpty(childErrors)) {
      childErrors["Id"] = this.getValue("Id");
    }

    return childErrors;
  }

  updateId(oldId, newId) {
    let obj = this.getObject(oldId);
    if (obj === null) {
      throw new Error(
        `The given Id ${oldId} does not exist, please provide a valid Id`
      );
    }
    this.validateNewId(newId);

    obj.currentJson["Id"] = newId;
    obj.id = newId;
    this.dataSet[newId] = this.dataSet[oldId];
    this.ds.cache.set(newId, this.ds.cache.get(oldId));
    this.ds.cache.delete(oldId);
    Reflect.deleteProperty(this.dataSet, oldId);

    //update newId to all the reference cell
    let referenceList = obj.constructor.manyToOneList;
    for (let referenceName of referenceList) {
      referenceName = referenceName.split("::")[0];
      let referenceId = obj.currentJson[referenceName];
      if (referenceId) {
        var referredObj = this.dataSet[referenceId];
        if (referredObj) {
          let oneToManyName = referenceName + "::" + obj.currentJson.Kind;
          let childList = referredObj[oneToManyName] || [];
          let index = childList.indexOf(oldId);
          if (index !== -1) {
            childList[index] = newId;
          }
        } else {
          throw new Error(`Instance not found for the Reference ${referenceName}
            with the value ${referenceId} in ${oldId}`);
        }
      }
    }

    //update newId to all the childlist
    let oneToManyList = obj.constructor.oneToManyList;
    for (let oneToManyName of oneToManyList) {
      let childIdList = obj.currentJson[oneToManyName] || [];
      let referenceName = oneToManyName.split("::")[0];
      for (let childId of childIdList) {
        let child = this.dataSet[childId];
        if (child) {
          if (child[referenceName]) {
            child[referenceName] = newId;
          } else {
            throw new Error(`Instance not found for the oneToMany ${oneToManyName}
          with the value ${childId}`);
          }
        }
      }
    }
    return this;
  }

  validateNewId(newId, idParams = {}) {
    let { excludedIdList = [] } = idParams;
    let model = this.getModel();
    let fieldList = model.getList("Model", "Field");
    for (let childModel of model.getList("Model", "Model")) {
      fieldList = fieldList.concat(childModel.getList("Model", "Field"));
    }

    fieldList = fieldList.filter((field) => !excludedIdList.includes(field.id));

    let idExists = fieldList.find(
      (field) => field.id.toLowerCase() === newId.toLowerCase().trim()
    );

    if (!idExists) {
      idExists = model
        .getList("Model", "Model")
        .find(
          (childModel) =>
            childModel.id.toLowerCase() === newId.toLowerCase().trim()
        );
    }

    if (!idExists) {
      let parentModelFieldList =
        fieldsWithInternalCache[MODEL_TO_PARENT_MODEL[model.getFlowType()]] ||
        [];
      let sysFields = excludedNames.concat(parentModelFieldList, {
        Id: this.getValue("Model")
      });
      let childModel = this.getModel(this.getValue("Model"))?.isChildModel();
      if (childModel) {
        sysFields.push({ Id: model.getValue("Id") });
      }
      idExists = sysFields.find(
        (field) => field["Id"].toLowerCase() === newId.toLowerCase().trim()
      );
    }
    if (idExists) {
      throw new Error(
        `The given Id ${newId} already exist, please provide a different Id`
      );
    }
  }

  getUniqueId(fieldId, count = 0, idParams = {}) {
    try {
      let newFieldId = count > 0 ? `${fieldId}_${count}` : fieldId;
      this.validateNewId(newFieldId, idParams);
      return newFieldId;
    } catch (err) {
      count = count + 1;
      return this.getUniqueId(fieldId, count, idParams);
    }
  }

  getTruncatedFieldId(fieldId = "") {
    if (fieldId.length > FIELD_ID.MAX_LIMIT) {
      return this.getUniqueId(fieldId.substring(0, FIELD_ID.TRUNCATE_LIMIT));
    }
    return this.getUniqueId(fieldId);
  }

  resetSetting() {}

  canDelete() {
    if (this.isSystemField()) return false;
    return this.get("Model").getList("Model", "Field").length > 1;
  }

  getObject(id, createNewIns) {
    if (!this.dataSet[id]) {
      return null;
    }
    if (this.ds.cache.get(id) && !createNewIns) {
      return this.ds.cache.get(id);
    }
    let kind = this.dataSet[id].Kind;

    var dynamicClass = this.getMetadata(kind, this.dataSet[id]);

    let ins = new dynamicClass(this.ds, id, this.context);
    this.ds.cache.set(id, ins);
    return ins;
  }

  changeFieldType(fieldType) {
    var id = this.getValue("Id");
    var newIns = null;
    this.dataSet[id].Type = fieldType;
    if (WIDGET_TYPE_FIELDS.includes(fieldType)) {
      this.dataSet[id].Widget = fieldType;
    } else {
      // eslint-disable-next-line prefer-reflect
      delete this.dataSet[id].Widget;
    }
    try {
      newIns = this.getObject(id, true);
      this.resetFieldSettings(newIns);
    } catch (err) {
      console.error("Cannot change field type : ", err);
      this.ds.revert();
    }
    return newIns;
  }

  resetFieldSettings(newFieldIns) {
    try {
      var newFieldTypes = Object.keys(newFieldIns.constructor.fieldTypes);
      let existingFieldProperties = Object.keys(this.constructor.fieldTypes);
      existingFieldProperties.forEach((fieldType) => {
        if (newFieldTypes.indexOf(fieldType) === -1) {
          Reflect.deleteProperty(newFieldIns.currentJson, fieldType);
        }
      });

      Reflect.deleteProperty(newFieldIns.currentJson, "Required");
      Reflect.deleteProperty(newFieldIns.currentJson, "DefaultValue");

      for (let oneToManyName of this.constructor.oneToManyList) {
        let [refName, childName] = oneToManyName.split("::");
        let childList = newFieldIns.getList(refName, childName);
        for (let child of childList) {
          newFieldIns.delete(childName, child.getValue("Id"), refName);
        }
      }

      var columnOneToManyList = [
        "Column::Appearance",
        "ColumnVisibility::Criteria"
      ];

      var column = newFieldIns.getFieldColumn();
      // Default Appearance Check
      if (column && column.getValue("Color")) {
        Reflect.deleteProperty(column.currentJson, "Color");
      }
      // Default Visibility Check
      if (column && column.getValue("IsHidden")) {
        Reflect.deleteProperty(column.currentJson, "IsHidden");
      }

      for (let oneToMany of columnOneToManyList) {
        let [refName, childName] = oneToMany.split("::");
        let childList = column.getList(refName, childName);
        for (let child of childList) {
          column.delete(childName, child.getValue("Id"), refName);
        }
      }
    } catch (err) {
      throw new Error("Error in reset field settings");
    }
  }
}
Field.setFieldTypes({
  Column: FieldTypes.kindOf("Column").isRequired,
  Type: FieldTypes.string.isRequired,
  Name: FieldTypes.string.isRequired,
  CreatedAt: FieldTypes.string,
  Required: FieldTypes.boolean,
  IsSystem: FieldTypes.boolean,
  Model: FieldTypes.kindOf("Model").isRequired,
  BlockNameChange: FieldTypes.boolean,
  Widget: FieldTypes.string
  // OverrideExpression: FieldTypes.boolean,
});

Field.setManyToOneList([
  "Column::Column", //will be used to create/delete field
  "Model::Model" //will be updated while creating field via column
]);

export class Expression extends BaseModel {
  getRootNode() {
    var nodeList = this.getList("Expression", "Node");
    if (nodeList.length > 0) {
      return nodeList[0];
    }
    console.error(" Expression does not have nodes");
    throw new Error(`Invalid Expression`);
  }
  getDataType() {
    return this.getRootNode().getValue("DataType");
  }
  getExpressionStr() {
    let rootNode = this.getList("Expression", "Node")[0];
    if (!rootNode) {
      return "";
    }
    return rootNode.deSerialize(); //this.getValue('ExpressionStr');
  }
}
Expression.setFieldTypes({
  Field: FieldTypes.kindOf("Field"),
  Condition: FieldTypes.kindOf("Condition"),
  RemoteLookup: FieldTypes.kindOf("RemoteLookup"),
  XPATH: FieldTypes.kindOf("RemoteLookup"),
  DataType: FieldTypes.string,
  ExpressionStr: FieldTypes.string
});
Expression.setOneToManyList(["Expression::Node"]);
Expression.setManyToOneList([
  "Field::Field", //will be used to create/delete Expression
  "Condition::Condition",
  "RemoteLookup::RemoteLookup",
  "XPATH::Property"
]);

export class Node extends BaseModel {
  isStaticNode() {
    return this.getValue("Type") === "Static";
  }

  isFieldNode() {
    return ["Field", "ParentField", "RefField"].includes(this.getValue("Type"));
  }

  isLookupFieldNode() {
    return ["Lookup", "ParentLookup"].includes(this.getValue("Type"));
  }

  isFunctionNode() {
    return ["Function", "ModelFunction"].includes(this.getValue("Type"));
  }

  isLookupFunction() {
    return this.getValue("Value") === "GetValue";
  }

  getNodeValue() {
    // we will not save field having empty strings/null values, so workarround to return empty string
    return this.getValue("Value") || "";
  }

  getChildNodes() {
    return this.getList("Node", "Node");
  }

  isChainedFunction() {
    return this.getValue("Syntax") === "Chain";
  }

  isInfixFunction() {
    return this.getValue("Syntax") === "Infix";
  }

  deSerializeFunctionNode() {
    let nodeStr = "";
    let childNodes = this.getChildNodes();
    if (this.isInfixFunction()) {
      let leftNode = childNodes[0];
      let rightNode = childNodes[1];
      nodeStr = `${leftNode.deSerialize()} ${this.getNodeValue().trim()} ${rightNode.deSerialize()}`;
      if (this.getValue("IsParenthesised")) {
        nodeStr = `(${nodeStr})`;
      }
    } else if (this.isChainedFunction()) {
      //parse chained function from right ---> left
      let firstArgStr = "";
      let nodeArgumentStr = "";
      let firstArg = childNodes[0];

      firstArgStr = `${firstArg.deSerialize()}${firstArgStr}`;

      if (childNodes.length > 1) {
        //first node is the first argument of chaining, and all other nodes are other arguments.
        for (let childNode of childNodes.slice(1)) {
          let value = `${childNode.deSerialize()}`;
          //add comma in argument list except for the last argument
          let shouldAddComma = childNode !== childNodes[childNodes.length - 1];
          if (shouldAddComma) {
            value = value + ", ";
          }
          nodeArgumentStr = `${nodeArgumentStr}${value}`;
        }
      }
      nodeStr = `${firstArgStr}.${this.getNodeValue()}(${nodeArgumentStr})`;
    } else if (this.isLookupFunction()) {
      nodeStr = `${childNodes[0].deSerialize()}.${childNodes[1].deSerialize()}`;
    } else {
      //utility functions
      let childNodeStr = "";
      for (let childNode of childNodes) {
        let value = childNode.deSerialize();
        //add comma in argument list except for the last argument
        let shouldAddComma = childNode !== childNodes[childNodes.length - 1];
        if (shouldAddComma) {
          value = value + ", ";
        }
        childNodeStr = `${childNodeStr}${value}`;
      }
      nodeStr = `${this.getNodeValue()}(${childNodeStr})`;
    }
    return nodeStr;
  }

  deSerializeStaticNode() {
    let nodeStr = this.getNodeValue();
    if (this.getValue("DataType") === DATA_TYPES.STRING) {
      nodeStr = `"${this.getNodeValue()}"`;
    }
    return nodeStr;
  }

  deSerializeFieldNode() {
    let nodeStr = this.getValue("Field") || this.getValue("RefField");
    if (this.getValue("Type") === "ParentField") {
      nodeStr = `Parent.${this.getValue("Field")}`;
    }
    return nodeStr;
  }

  deSerializeLookupFieldNode() {
    let nodeStr = this.getValue("Field");
    if (this.getValue("Type") === "RefField") {
      return this.getValue("RefField");
    } else if (this.getValue("Type") === "ParentLookup") {
      nodeStr = `Parent.${this.getValue("Field")}`;
    }
    let referredFields = this.getValue("LookUpPath").join(".");
    nodeStr = `${nodeStr}.${referredFields}`;
    return nodeStr;
  }

  deSerialize() {
    let nodeStr = "";
    if (this.isFunctionNode()) {
      nodeStr = this.deSerializeFunctionNode();
    } else if (this.isStaticNode()) {
      nodeStr = this.deSerializeStaticNode();
    } else if (this.isFieldNode()) {
      nodeStr = this.deSerializeFieldNode();
    } else if (this.isLookupFieldNode()) {
      nodeStr = this.deSerializeLookupFieldNode();
    }
    return nodeStr;
  }
}
Node.setFieldTypes({
  Expression: FieldTypes.kindOf("Expression"),
  Node: FieldTypes.kindOf("Node"),
  Type: FieldTypes.string.isRequired,
  Field: FieldTypes.kindOf("Field"),
  RefField: FieldTypes.kindOf("Field"),
  RefModel: FieldTypes.string,
  RefRootModel: FieldTypes.string,
  RefFlowType: FieldTypes.string,
  IsSystemField: FieldTypes.boolean,
  FieldModel: FieldTypes.kindOf("Model"),
  FieldRootModel: FieldTypes.kindOf("Model"),
  Value: FieldTypes.string,
  DataType: FieldTypes.string,
  Syntax: FieldTypes.string, //Infix or Chain. Default function call ()
  Category: FieldTypes.string,
  LookUpPath: FieldTypes.array,
  FieldRefCount: FieldTypes.number,
  FunctionFlowType: FieldTypes.string,
  IsParenthesised: FieldTypes.boolean,
  LeadingWS: FieldTypes.number,
  TrailingWS: FieldTypes.number,
  FlowId: FieldTypes.string, // store flow id of getValue expr
  FlowType: FieldTypes.string, // flow type for getValue expressions
  SourceFlowId: FieldTypes.string, // storing flow id in getValueNode
  SourceFlowType: FieldTypes.string // storing flow type in getValueNode
});
Node.setOneToManyList(["Node::Node"]);
Node.setManyToOneList([
  "Expression::Expression", //will be used to create/delete root Node
  "Node::Node", //will be used to create/delete Node
  "Field::Field" //will be used for field dependency
]);

export class Appearance extends BaseModel {
  validate() {
    var childErrors = {};

    var propertyErrors = this.validateProperties();
    var oneToManyErrors = this.validateOneToMany();
    if (propertyErrors.length > 0) {
      childErrors["Properties"] = propertyErrors;
    }
    if (!isEmpty(oneToManyErrors)) {
      childErrors["Children"] = oneToManyErrors;
    }
    if (!isEmpty(childErrors)) {
      childErrors["Id"] = this.getValue("Id");
    }

    return childErrors;
  }
}
Appearance.setOneToManyList(["Appearance::Condition"]);
Appearance.setManyToOneList([
  "Column::Column" // to create / delete appearance
]);
Appearance.setFieldTypes({
  Column: FieldTypes.kindOf("Column"),
  FontSize: FieldTypes.string,
  Color: FieldTypes.string
});

export class Criteria extends BaseModel {
  hasCondition(refName = "Criteria") {
    return !!this.getList(refName, "Condition").length;
  }

  isFieldValidationCriteria() {
    return this.getValue("FieldValidation");
  }

  isVisibilityCriteria() {
    return this.getValue("ColumnVisibility");
  }

  getConditions() {
    return this.getList("Criteria", "Condition");
  }

  isQueryDefinitionCriteria() {
    return this.getValue("QueryDefinition");
  }

  getCriteriaType() {
    if (this.isFieldValidationCriteria()) {
      return "Validation";
    } else if (this.isVisibilityCriteria()) {
      return "Visibility";
    } else if (this.isQueryDefinitionCriteria()) {
      return "QueryDefinition";
    } else {
      return null;
    }
  }

  validate() {
    var errors = {};

    var propertyErrors = this.validateProperties();
    if (propertyErrors.length > 0) {
      errors["Properties"] = propertyErrors;
    }

    var criteriaType = this.getCriteriaType();

    var children = {};
    children["Condition"] = [];
    this.validateCondition(criteriaType, children);

    if (!isEmpty(children["Condition"])) {
      errors["SubKind"] = criteriaType;
      errors["Children"] = children;
    }

    if (!isEmpty(errors)) {
      errors["Id"] = this.getValue("Id");
    }

    return errors;
  }

  validateCondition(criteriaType, errors) {
    var conditions = this.getConditions();

    if (!conditions.length) {
      errors["Condition"].push("No condition");
    }

    conditions.forEach((condition) => {
      var conditionErrors = condition.validate(criteriaType);
      if (!isEmpty(conditionErrors)) {
        errors["Condition"].push(conditionErrors);
      }
    });
  }
}
Criteria.setOneToManyList(["Criteria::Condition", "Criteria::Criteria"]);
Criteria.setManyToOneList([
  "Criteria::Criteria", //will be used to create nested criteria
  "FieldValidation::Field", //will be used to create/delete field validation,
  "ColumnVisibility::Column", //will be used to create/delete column visibility,
  "QueryDefinition::QueryDefinition" //will be used to create/delete filters for field,
]);
Criteria.setFieldTypes({
  ColumnVisibility: FieldTypes.kindOf("Column"),
  QueryDefinition: FieldTypes.kindOf("QueryDefinition"),
  Criteria: FieldTypes.kindOf("Criteria"),
  FieldValidation: FieldTypes.kindOf("Field"),
  IsOR: FieldTypes.boolean,
  IsHidden: FieldTypes.boolean // tells visibility of the columns
});

export class Condition extends BaseModel {
  validate(criteriaType) {
    var errors = {};
    var propertyErrors = [];

    var operator = this.getValue("Operator");

    if (!operator) {
      propertyErrors.push("Operator");
    }
    var rhsType = this.getValue("RHSType");

    if (this.getValue("HasArguments") || rhsType) {
      var rhsValue = this.getValue("RHSValue");
      var rhsField = this.getValue("RHSField");
      var lhsOwnField = this.getValue("LHSOwnField");
      var lhsField = this.getValue("LHSField");

      if (!rhsType) {
        propertyErrors.push("RHSType");
      }

      if (criteriaType === "Visibility" && !lhsOwnField) {
        propertyErrors.push("LHSOwnField");
      }

      if (criteriaType === "QueryDefinition" && !lhsField) {
        propertyErrors.push("LHSField");
      }

      if (rhsType === RHS_TYPE.VALUE.Name) {
        if (!rhsValue && rhsValue !== 0) {
          propertyErrors.push("RHSValue");
        }
        if (Array.isArray(rhsValue) && rhsValue.length === 0) {
          propertyErrors.push("RHSValue");
        }
      }
      if (rhsType === RHS_TYPE.FIELD.Name && !rhsField) {
        propertyErrors.push("RHSField");
      }
    }

    if (!isEmpty(propertyErrors)) {
      errors["Id"] = this.getValue("Id");
      errors["Properties"] = propertyErrors;
    }

    return errors;
  }
}
Condition.setManyToOneList([
  "Criteria::Criteria", //will be used to create criteria
  "RHSField::Field", // will be used for field dependency check
  "LHSOwnField::Field", // will be used for field dependency check in visibility, permission,
  "Appearance::Appearance" // Appearance's condition
]);
Condition.setFieldTypes({
  Criteria: FieldTypes.kindOf("Criteria"),
  Appearance: FieldTypes.kindOf("Appearance"),
  LHSValue: FieldTypes.string,
  LHSField: FieldTypes.kindOf("Field"),
  //will only apply when condition holds the same model field ( eg:- in validation, appearance, visibility)
  LHSOwnField: FieldTypes.kindOf("Field"),
  Operator: FieldTypes.string,
  RHSType: FieldTypes.string,
  RHSField: FieldTypes.kindOf("Field"),
  RHSValue: FieldTypes.oneOfType([FieldTypes.string, FieldTypes.array]),
  RHSExpression: FieldTypes.kindOf("Expression"),
  HasArguments: FieldTypes.boolean,
  ErrorMessage: FieldTypes.string
});

export class QueryDefinition extends BaseModel {
  getLookupFields() {
    return this.getValue("LookupField");
  }

  getQueryDefField() {
    return this.get("Field");
  }

  getQueryDefCriteria() {
    return this.getList("QueryDefinition", "Criteria");
  }

  validate() {
    var errors = {};
    var propertyErrors = [];

    var fieldType = this.getQueryDefField().getValue("Type");
    var lhsModel = this.getValue("LHSModel");

    if (!lhsModel) {
      propertyErrors.push("LHSModel");
    }

    if (fieldType === FIELD_TYPES.REFERENCE) {
      var lookup = this.getValue("LookupField");
      if (!lookup?.length) {
        propertyErrors.push("LookupField");
      }
    }

    if (fieldType === FIELD_TYPES.AGGREGATION) {
      let aggregationType = this.getValue("AggregateType");
      let aggregateField = this.getValue("AggregateField");
      if (!aggregateField) {
        propertyErrors.push("AggregateField");
      }
      if (!aggregationType) {
        propertyErrors.push("AggregateType");
      }
    }

    if (!isEmpty(propertyErrors)) {
      errors["Properties"] = propertyErrors;
    }

    var oneToManyErrors = this.validateOneToMany();
    if (!isEmpty(oneToManyErrors)) {
      errors["Children"] = oneToManyErrors;
    }

    if (!isEmpty(errors)) {
      errors["Id"] = this.getValue("Id");
    }

    return errors;
  }
}
QueryDefinition.setOneToManyList(["QueryDefinition::Criteria"]);
QueryDefinition.setManyToOneList([
  "Field::Field",
  "Model::Model", //for dataset vie model
  "SelfAggregateField::Field" //childModel field aggregation dependency
]);
QueryDefinition.setFieldTypes({
  Field: FieldTypes.kindOf("Field"),
  Model: FieldTypes.kindOf("Model"),
  LHSModel: FieldTypes.kindOf("Model"),
  LHSRootModel: FieldTypes.kindOf("Model"),
  LHSModelApplicationId: FieldTypes.kindOf("Model"),
  LookupField: FieldTypes.array,
  HiddenField: FieldTypes.array,
  SortBy: FieldTypes.array,
  AutoFill: FieldTypes.boolean,
  FlowType: FieldTypes.string,
  AllFields: FieldTypes.boolean,

  AggregateField: FieldTypes.kindOf("Field"),
  AggregateType: FieldTypes.string,
  SelfAggregateField: FieldTypes.kindOf("Field")
});

export class Property extends BaseModel {
  validate() {
    var errors = {};
    var propertyErrors = [];

    var valueType = this.getValue("ValueType");
    var name = this.getValue("Name");
    var valueField = this.getValue("ValueField");
    var value = this.getValue("Value");
    var expressionList = this.getList("Property", "Expression");

    if (!valueType && !value) {
      propertyErrors.push("ValueType");
      propertyErrors.push("Value");
    }

    if (!name) {
      propertyErrors.push("Name");
    }

    if (valueType === "Field" && !valueField) {
      propertyErrors.push("ValueField");
    }

    if (valueType === "Value" && !value) {
      propertyErrors.push("Value");
    }

    if (valueType === "Expression" && !expressionList.length) {
      propertyErrors.push("Expression");
    }

    if (!isEmpty(propertyErrors)) {
      errors["Id"] = this.getValue("Id");
      errors["Properties"] = propertyErrors;
    }

    return errors;
  }
}
Property.setManyToOneList([
  "ValueField::Field", // used for field dependency check
  "Header::RemoteLookup",
  "Body::RemoteLookup"
]);
Property.setFieldTypes({
  Field: FieldTypes.kindOf("Field"),
  Header: FieldTypes.kindOf("RemoteLookup"),
  Body: FieldTypes.kindOf("RemoteLookup"),
  Type: FieldTypes.string,
  Name: FieldTypes.string,
  Value: FieldTypes.string,
  ValueField: FieldTypes.kindOf("Field"),
  ValueType: FieldTypes.string
});

class RemoteLookup extends BaseModel {
  isTypeText() {
    return this.getResultType() === "Text";
  }

  isTypeJSON() {
    return this.getResultType() === "JSON";
  }

  isTypeXML() {
    return this.getResultType() === "XML";
  }

  isResponseTypeJSON() {
    return this.getResponseType() === "JSON";
  }

  isResponseTypeXML() {
    return this.getResponseType() === "XML";
  }

  isResponseTypeText() {
    return this.getResponseType() === "Text";
  }

  isAutoSelect() {
    return this.getTrigger() !== "Manual";
  }

  getResultType() {
    return this.getValue("ResultType");
  }

  getResponseType() {
    return this.getValue("ResponseType");
  }

  getTrigger() {
    return this.getValue("Trigger");
  }

  getUrl() {
    return this.getList("RemoteLookup", "Expression")[0];
  }

  getHeader() {
    return this.getList("Header", "Property");
  }

  getBody() {
    return this.getList("Body", "Property");
  }

  getXPATH() {
    return this.getList("XPATH", "Expression")[0];
  }

  createProperty(propData, refName) {
    return this.create("Property", propData, refName);
  }

  updateProperty(propertyId, propData = {}) {
    return this.getProperty(propertyId).updateMulti(propData);
  }

  getProperty(propertyId) {
    return this.getModel(propertyId);
  }

  deleteProperty(propertyId, refName) {
    return this.delete("Property", propertyId, refName);
  }

  validateProperties() {
    var propertyErrors = super.validateProperties();
    if (!this.getUrl()) {
      propertyErrors.push("Url");
    }

    if (!this.getResponseType()) {
      propertyErrors.push("ResponseType");
    }

    if (this.isResponseTypeJSON() && !this.getXPATH()) {
      propertyErrors.push("XPath");
    }

    if (this.isResponseTypeXML() && !this.getXPATH()) {
      propertyErrors.push("XPath");
    }

    return propertyErrors;
  }

  validate() {
    var childErrors = {};

    var propertyErrors = this.validateProperties();
    var oneToManyErrors = this.validateOneToMany();
    if (propertyErrors.length > 0) {
      childErrors["Properties"] = propertyErrors;
    }
    if (!isEmpty(oneToManyErrors)) {
      childErrors["Children"] = oneToManyErrors;
    }
    if (!isEmpty(childErrors)) {
      childErrors["Id"] = this.getValue("Id");
    }

    return childErrors;
  }
}
RemoteLookup.setOneToManyList([
  "Header::Property",
  "Body::Property",
  "RemoteLookup::Expression", // for URL
  "XPATH::Expression" // for JSON path
]);
RemoteLookup.setManyToOneList(["Field::Field"]);
RemoteLookup.setFieldTypes({
  Field: FieldTypes.kindOf("Field").isRequired,
  Method: FieldTypes.string, // GET / POST
  ResponseType: FieldTypes.string, // Text / JSON / XML
  ResultType: FieldTypes.string, // Text / JSON / XML
  Trigger: FieldTypes.string // Auto / Manual
});

export class Event extends BaseModel {
  getScript() {
    return this.getValue("Script");
  }

  getTrigger() {
    return this.getValue("Trigger");
  }

  setScript(script) {
    this.update("Script", script);
  }

  setTrigger(trigger) {
    this.update("Trigger", trigger);
  }

  validate() {
    var childErrors = {};

    var propertyErrors = this.validateProperties();
    var oneToManyErrors = this.validateOneToMany();
    if (propertyErrors.length > 0) {
      childErrors["Properties"] = propertyErrors;
    }
    if (!isEmpty(oneToManyErrors)) {
      childErrors["Children"] = oneToManyErrors;
    }
    if (!isEmpty(childErrors)) {
      childErrors["Id"] = this.getValue("Id");
    }

    return childErrors;
  }
}
Event.setFieldTypes({
  Trigger: FieldTypes.string,
  Script: FieldTypes.string,
  Column: FieldTypes.kindOf("Column"),
  Field: FieldTypes.kindOf("Field")
});

Event.setManyToOneList(["Column::Column", "Field::Field"]);

export class FormModel extends BaseModel {
  constructor(dataStore, id, context) {
    super(dataStore, id, context);
    this.parentModelFields = [];
    console.info("get parent fields for ", id);
    this.getSystemFields(this.getFlowType());
  }

  getFlowType() {
    return (
      (this.isChildModel()
        ? this.getModel().getFlowType()
        : this.getValue("FlowType")) || FLOW_TYPE.PROCESS
    );
  }

  getParentModel() {
    return this.isChildModel()
      ? SYSTEM_MODELS.SYSTEM
      : MODEL_TO_PARENT_MODEL[this.getFlowType()];
  }

  isChildModel() {
    return this.getValue("Model");
    //  || this.getValue("Id").startsWith("Model_");
  }

  isProcess() {
    if (this.getFlowType()) return this.getFlowType() === FLOW_TYPE.PROCESS;
    //for older processes which doesn't have FlowType field
    return true;
  }

  isCase() {
    return this.getFlowType() === FLOW_TYPE.CASE;
  }

  getFieldDetails(fieldList, fieldName) {
    // TODO. Need to redesign this code. Duplicate code with slight variation.
    let field = fieldList.filter(
      (field) => field["Id"].toUpperCase() === fieldName.toUpperCase()
    );
    if (field.length > 0) {
      return {
        Id: field[0]["Id"],
        Type: field[0]["Type"],
        Name: field[0]["Name"],
        SourceFlowId: field[0]["SourceFlowId"],
        SourceFlowType: field[0]["SourceFlowType"]
      };
    }
  }

  getFieldDetailByName(fieldName) {
    var fieldList = this.getList("Model", "Field");
    var field = fieldList.filter(
      (field) => field.getValue("Id").toUpperCase() === fieldName.toUpperCase()
    );
    if (field.length > 0) {
      return {
        Id: field[0].getValue("Id"),
        Type: field[0].getValue("Type"),
        Name: field[0].getValue("Name")
      };
    }
    field = (fieldsCache[this.getParentModel()] || []).filter((field) => {
      return field["Id"].toUpperCase() === fieldName.toUpperCase();
    });
    if (field.length > 0) {
      return {
        Id: field[0]["Id"],
        Type: field[0]["Type"],
        Name: field[0]["Name"],
        IsSystemField: true
      };
    }
    throw new Error(`${fieldName} is not part of ${this.getValue("Name")}`);
  }

  validateUserFieldFetchPath(fetchFieldId, modelId = "User") {
    let field = this.getFieldDetails(fieldsCache[modelId] || [], fetchFieldId);
    if (!field) {
      throw new Error(
        `Field '${fetchFieldId}' not found in the ${modelId} model`
      );
    }
    return field;
  }

  getPublishedAt() {
    return (
      this.dataSet.PublishedAt ||
      this.getValue("PublishedAt") ||
      this.getModel(this.getValue("Model")).getValue("PublishedAt")
    );
  }

  getStages() {
    return this.getList("Model", "Stage");
  }

  updateSavedAt(lastSavedAt) {
    this.dataSet._last_saved_at = lastSavedAt;
  }

  updateModelId(oldId, newId) {
    let obj = this.getObject(oldId);
    if (obj === null) {
      throw new Error(
        `The given Id ${oldId} does not exist, please provide a valid Id`
      );
    }
    this.validateNewModelId(newId);

    obj.currentJson["Id"] = newId;
    obj.id = newId;
    this.dataSet[newId] = this.dataSet[oldId];
    this.ds.cache.set(newId, this.ds.cache.get(oldId));
    this.ds.cache.delete(oldId);
    Reflect.deleteProperty(this.dataSet, oldId);

    //update newId to all the reference cell
    let referenceList = obj.constructor.manyToOneList;
    for (let referenceName of referenceList) {
      referenceName = referenceName.split("::")[0];
      let referenceId = obj.currentJson[referenceName];
      if (referenceId) {
        var referredObj = this.dataSet[referenceId];
        if (referredObj) {
          let oneToManyName = referenceName + "::" + obj.currentJson.Kind;
          let childList = referredObj[oneToManyName] || [];
          let index = childList.indexOf(oldId);
          if (index !== -1) {
            childList[index] = newId;
          }
        } else {
          throw new Error(`Instance not found for the Reference ${referenceName}
            with the value ${referenceId} in ${oldId}`);
        }
      }
    }

    //update newId to all the childlist
    let oneToManyList = obj.constructor.oneToManyList;
    for (let oneToManyName of oneToManyList) {
      let childIdList = obj.currentJson[oneToManyName] || [];
      let referenceName = oneToManyName.split("::")[0];
      for (let childId of childIdList) {
        let child = this.dataSet[childId];
        if (child) {
          if (child[referenceName]) {
            child[referenceName] = newId;
          } else {
            throw new Error(`Instance not found for the oneToMany ${oneToManyName}
          with the value ${childId}`);
          }
        }
      }
    }

    //update model id for model fields
    let model = this.getModel(newId);
    let fieldList = model.getList("Model", "Field");
    for (let field of fieldList) {
      // field.update("Model", newId);
      field.currentJson["Model"] = newId;
    }

    return this;
  }

  validateNewModelId(newId, idParams = {}) {
    let { excludedIdList = [] } = idParams;
    let model = this.getModel();
    let fieldList = model.getList("Model", "Field");
    for (let childModel of model.getList("Model", "Model")) {
      fieldList = fieldList.concat(childModel.getList("Model", "Field"));
    }
    let idExists = fieldList.find(
      (field) => field.id.toLowerCase() === newId.toLowerCase().trim()
    );

    if (!idExists) {
      let childModelList = model.getList("Model", "Model");
      childModelList.filter((model) => !excludedIdList.includes(model.id));
      idExists = childModelList.find(
        (childModel) =>
          childModel.id.toLowerCase() === newId.toLowerCase().trim()
      );
    }

    if (!idExists) {
      let parentModelFieldList =
        fieldsWithInternalCache[MODEL_TO_PARENT_MODEL[model.getFlowType()]] ||
        [];
      let sysFields = excludedNames.concat(parentModelFieldList, {
        Id: this.getValue("Model")
      });
      let childModel = this.isChildModel();
      if (childModel) {
        sysFields.push({ Id: model.getValue("Id") });
      }
      idExists = sysFields.find(
        (field) => field["Id"].toLowerCase() === newId.toLowerCase().trim()
      );
    }
    if (idExists) {
      throw new Error(
        `The given Id ${newId} already exist, please provide a different Id`
      );
    }
  }

  getUniqueModelId(modelId, count = 0, idParams = {}) {
    try {
      let newModelId = count > 0 ? `${modelId}_${count}` : modelId;
      this.validateNewModelId(newModelId, idParams);
      return newModelId;
    } catch (err) {
      count = count + 1;
      return this.getUniqueModelId(modelId, count, idParams);
    }
  }

  getTruncatedModelId(modelId) {
    if (modelId.length > CHILDTABLE_ID.MAX_LIMIT) {
      return this.getUniqueModelId(
        modelId.substring(0, CHILDTABLE_ID.TRUNCATE_LIMIT)
      );
    }
    return this.getUniqueModelId(modelId);
  }

  getCreatedAt() {
    return this.getValue("CreatedAt");
  }

  getModelRows() {
    return this.getList("Model", "Row");
  }

  isPage() {
    let rows = this.getModelRows();
    if (rows.length > 0) {
      let column = rows[0].getList("Row", "Column")[0];
      if (column.getValue("Type") === COLUMN_TYPES.PAGE) {
        return true;
      }
    }

    return false;
  }

  validateForm() {
    let rows = this.getModelRows();
    var errors = {};
    rows.forEach((modelRow) => {
      var column = modelRow.getColumns()[0];
      if (column.getValue("Type") === COLUMN_TYPES.SECTION) {
        let sectionValidation = column.validate();
        Object.values(sectionValidation[column.id]).length > 0 &&
          Object.assign(errors, sectionValidation);

        var sectionRows = column.getRows();
        sectionRows.forEach((row) => {
          var fieldColumns = row.getColumns();
          fieldColumns.forEach((field) => {
            var fieldErrors = field.validate();
            Object.values(fieldErrors[field.id]).length > 0 &&
              Object.assign(errors, fieldErrors);
          });
        });
      } else if (column.getValue("Type") === COLUMN_TYPES.MODEL) {
        let tableValidation = column.validate();
        Object.values(tableValidation[column.id]).length > 0 &&
          Object.assign(errors, tableValidation);

        var tableModel = column.getTableModel();
        var tableRow = tableModel.getModelRows()[0];
        var tableFieldColumns = tableRow.getColumns();
        tableFieldColumns.forEach((field) => {
          var fieldErrors = field.validate();
          Object.values(fieldErrors[field.id]).length > 0 &&
            Object.assign(errors, fieldErrors);
        });
      }
    });

    return errors;
  }
}

FormModel.setFieldTypes({
  Name: FieldTypes.string.isRequired,
  Description: FieldTypes.string,
  FlowType: FieldTypes.string,
  Column: FieldTypes.kindOf("Column").isRequired,
  Model: FieldTypes.kindOf("Model"),
  PublishedAt: FieldTypes.string,
  CreatedAt: FieldTypes.string
});
FormModel.setOneToManyList(["Model::Row", "Model::QueryDefinition"]);
// ManyToOne naming convention
// ReferenceKeyName::ReferenceModelName
FormModel.setManyToOneList([
  "Column::Column", // will be used to create/delete child model
  "Model::Model" //Will be update while creating child model via column
]);

registerFormModels();
